import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Check, Smartphone, ShieldOff } from "lucide-react";
import AuthPageLayout from "./AuthPageLayout";
import {
  DevOtpHint,
  EMAIL_RE,
  ErrorText,
  MOBILE_RE,
  OtpInput,
  VerifiedBadge,
  ctaClass,
  fieldClass,
  labelClass,
  useCountdown,
} from "./OtpAuthParts";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { setUserTokens } from "@/lib/userSession";

const inputClass =
  "w-full px-4 py-3.5 rounded-lg border border-line bg-card outline-none text-foreground text-[14px] focus:border-primary focus:ring-2 focus:ring-cm-blue/10 transition disabled:bg-surface-2";

function buildSteps(_otpRequired) {
  // Email ownership OTP is always required; admin flag only toggles mobile OTP.
  return [
    { k: 1, label: "Account" },
    { k: 2, label: "Email" },
    { k: 3, label: "Mobile" },
  ];
}

function Stepper({ current, steps }) {
  return (
    <div className="flex items-center gap-1.5 mb-8 overflow-x-auto cm-no-scrollbar">
      {steps.map((s, i) => {
        const done = s.k < current;
        const active = s.k === current;
        return (
          <div key={s.k} className="flex items-center gap-1.5 shrink-0">
            <div
              className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-semibold transition
                ${done ? "brand-gradient text-primary-foreground" : active ? "brand-gradient text-primary-foreground ring-4 ring-cm-blue/15" : "bg-surface-2 text-muted-foreground"}`}
              data-testid={`step-indicator-${s.k}`}
            >
              {done ? <Check size={13} /> : s.k}
            </div>
            <div className={`text-[11px] font-medium ${active ? "text-foreground" : "text-muted-foreground"}`}>
              {s.label}
            </div>
            {i < steps.length - 1 && (
              <div className={`h-[2px] w-6 rounded ${done ? "brand-gradient" : "bg-surface-2"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Input({ label, hint, error, testid, right, ...props }) {
  return (
    <div>
      {label && <label className="text-[13px] font-semibold text-foreground">{label}</label>}
      <div className="relative mt-1.5">
        <input
          {...props}
          data-testid={testid}
          className={`${inputClass} ${error ? "border-down/60 focus:border-down/60 focus:ring-red-400/10" : ""} ${
            right ? "pr-11" : ""
          } ${props.className || ""}`}
        />
        {right}
      </div>
      {hint && <p className="mt-1 text-[12px] text-muted-foreground">{hint}</p>}
      {error && <p className="mt-1 text-[12px] text-down">{error}</p>}
    </div>
  );
}

function PrimaryBtn({ loading, children, testid, ...props }) {
  return (
    <button
      type="submit"
      disabled={loading || props.disabled}
      data-testid={testid}
      className="w-full mt-2 py-3.5 rounded-lg brand-gradient hover:brightness-110 transition text-primary-foreground font-semibold text-[15px] disabled:opacity-60 flex items-center justify-center gap-2 shadow-[0_12px_32px_-14px_rgba(40, 202, 113,0.55)]"
      {...props}
    >
      {loading && <Loader2 size={16} className="animate-spin" />}
      {children}
    </button>
  );
}

function ErrorBox({ msg }) {
  if (!msg) return null;
  return (
    <div data-testid="register-error"
         className="text-[13px] text-down bg-down/10 border border-down/30 rounded-lg px-4 py-3">
      {msg}
    </div>
  );
}

/* ===================== Verify Later Popup ===================== */
function VerifyLaterPopup({ onContinue }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card rounded-2xl border border-border shadow-2xl max-w-[420px] w-full p-6">
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-warning/10 border border-warning/30 flex items-center justify-center">
            <ShieldOff size={32} className="text-warning" />
          </div>
          <div>
            <h3 className="text-[18px] font-semibold text-foreground mb-2">Mobile verification skipped</h3>
            <p className="text-[13px] text-muted-foreground leading-relaxed">
              Your email and phone number are saved. Phone verification is not required to sign up.
              You can verify the number anytime from your{" "}
              <strong className="text-foreground">Profile</strong> page.
            </p>
          </div>
          <div className="w-full space-y-2">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-2 border border-border">
              <Smartphone size={16} className="text-muted-foreground shrink-0" />
              <p className="text-[12px] text-muted-foreground text-left">
                Mobile verification: go to <strong>Profile → Verify Mobile</strong>
              </p>
            </div>
          </div>
          <button
            onClick={onContinue}
            className="w-full py-3.5 rounded-lg brand-gradient text-primary-foreground font-semibold text-[15px] hover:brightness-110 transition"
            data-testid="verify-later-continue"
          >
            Continue to app
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================== Step 3: Mobile OTP =========================== */
function Step3Mobile({ onDone, otpRequired }) {
  const [phase, setPhase] = useState("enter");
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [otpLength, setOtpLength] = useState(6);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function sendOtp(e) {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      if (otpRequired) {
        const { data } = await api.post("/api/auth/mobile/send-otp", { mobile_number: mobile });
        setDevOtp(data?.dev_mobile_otp || "");
        setOtpLength(Number.isInteger(data?.otp_length) && data.otp_length >= 4 ? data.otp_length : 6);
        setPhase("otp");
      } else {
        await api.post("/api/auth/mobile/save-number", { mobile_number: mobile });
        onDone({ skippedOtp: true });
      }
    }
    catch (e2) { setErr(getErrorMessage(e2)); }
    finally { setLoading(false); }
  }

  async function verifyOtp(e) {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await api.post("/api/auth/mobile/verify-otp", { code }); onDone({ skippedOtp: false }); }
    catch (e2) { setErr(getErrorMessage(e2)); }
    finally { setLoading(false); }
  }

  if (phase === "otp" && otpRequired) {
    return (
      <form onSubmit={verifyOtp} className="space-y-5" data-testid="step3-form-otp">
        <h2 className="text-[22px] font-semibold text-foreground tracking-tight">Verify your mobile</h2>
        <p className="text-muted-foreground text-[14px] -mt-2">
          Enter the {otpLength}-digit code for <span className="font-semibold text-foreground">{mobile}</span>.
        </p>
        <OtpDevHint
          label="Mobile"
          code={devOtp}
          note={devOtp ? "Dev mode — use the code above (SMS provider disabled)." : null}
        />
        <Input label="Mobile OTP" inputMode="numeric" maxLength={otpLength} required
               value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
               testid="step3-code" placeholder={"_".repeat(otpLength)}
               className="text-center tracking-[0.4em] font-mono text-[18px]" />
        <ErrorBox msg={err} />
        <PrimaryBtn loading={loading} testid="step3-submit">Verify mobile</PrimaryBtn>
        <button type="button" onClick={() => setPhase("enter")}
                className="w-full text-[13px] text-muted-foreground hover:underline">
          ← Change number
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={sendOtp} className="space-y-5" data-testid="step3-form">
      <h2 className="text-[22px] font-semibold text-foreground tracking-tight">
        {otpRequired ? "Verify your mobile" : "Add your mobile number"}
      </h2>
      <p className="text-muted-foreground text-[14px] -mt-2">
        {otpRequired
          ? "We need your mobile number for security alerts (required by FIU-IND)."
          : "Add your mobile number. You can verify it later from your profile."}
      </p>
      {!otpRequired && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-warning/10 border border-warning/30">
          <ShieldOff size={16} className="text-warning mt-0.5 shrink-0" />
          <p className="text-[12px] text-warning">
            OTP verification is currently disabled by your administrator. Your number will be saved but not verified until you verify it from your profile.
          </p>
        </div>
      )}
      <Input label="Mobile number (India)" type="tel" required value={mobile}
             onChange={(e) => setMobile(e.target.value.replace(/[^\d+]/g, ""))}
             testid="step3-mobile" placeholder="10-digit number" maxLength={13} />
      <ErrorBox msg={err} />
      <PrimaryBtn loading={loading} testid="step3-send-otp">
        {otpRequired ? "Send OTP" : "Save number & continue"}
      </PrimaryBtn>
    </form>
  );
}

function VerifyField({ id, channel, labelText, placeholder, verified, onValueChange, onVerified }) {
  const [value, setValue] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [devOtp, setDevOtp] = useState("");
  const [otpLength, setOtpLength] = useState(6);
  const [err, setErr] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [left, restartCountdown] = useCountdown(sent);

  const target = channel === "email" ? value.trim().toLowerCase() : value.replace(/\D/g, "");
  const valid = channel === "email" ? EMAIL_RE.test(target) : MOBILE_RE.test(target);

  async function send() {
    setSending(true);
    setErr("");
    try {
      const { data } = await api.post("/api/auth/otp/send", { channel, target, purpose: "signup" });
      setDevOtp(data?.dev_otp || "");
      setOtpLength(Number.isInteger(data?.otp_length) && data.otp_length >= 4 ? data.otp_length : 6);
      setSent(true);
      restartCountdown();
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setSending(false);
    }
  }

  async function check(code) {
    if (code.length < otpLength) return;
    setChecking(true);
    setErr("");
    try {
      const { data } = await api.post("/api/auth/otp/verify-contact", { channel, target, code });
      onVerified(data.target, data.proof);
    } catch (e) {
      setErr(getErrorMessage(e));
      setAttempt((n) => n + 1);
    } finally {
      setChecking(false);
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2">
        <label className={labelClass} htmlFor={id}>{labelText}</label>
        {verified && <VerifiedBadge />}
      </div>
      <div className="mt-1.5 flex gap-2">
        <input
          id={id}
          className={`${fieldClass} mt-0`}
          value={value}
          disabled={verified || sent}
          placeholder={placeholder}
          inputMode={channel === "email" ? "email" : "numeric"}
          autoComplete={channel === "email" ? "email" : "tel-national"}
          maxLength={channel === "email" ? 254 : 14}
          data-testid={`${id}-input`}
          onChange={(e) => {
            setValue(e.target.value);
            onValueChange?.(e.target.value);
          }}
        />
        {!verified && !sent && (
          <button
            type="button"
            className="shrink-0 rounded-lg border border-border bg-surface-2 px-3 text-xs font-medium text-foreground transition hover:border-primary disabled:opacity-40"
            disabled={!valid || sending}
            onClick={send}
            data-testid={`${id}-send-otp`}
          >
            {sending ? "Sending…" : "Send OTP"}
          </button>
        )}
      </div>
      {sent && !verified && (
        <div className="mt-2 space-y-2 page-fade">
          {channel === "email" && !devOtp ? (
            <p className="text-[12px] text-muted-foreground">
              Check your inbox and your <strong>spam folder</strong>.
            </p>
          ) : null}
          <DevOtpHint code={devOtp} />
          <OtpInput key={attempt} length={otpLength} error={attempt > 0 && !!err} onChange={check} testId={`${id}-otp`} />
          <ErrorText msg={err} />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => { setSent(false); setDevOtp(""); setErr(""); }}
            >
              Change
            </button>
            {checking ? (
              <span>Verifying…</span>
            ) : left > 0 ? (
              <span className="tabular-nums">Resend in 0:{String(left).padStart(2, "0")}</span>
            ) : (
              <button type="button" className="text-primary hover:underline" onClick={send} disabled={sending}>
                Resend code
              </button>
            )}
          </div>
        </div>
      )}
      {!sent && !verified && <ErrorText msg={err} />}
    </div>
  );
}

function dobToIso({ d, m, y }) {
  if (!/^\d{1,2}$/.test(d) || !/^\d{1,2}$/.test(m) || !/^\d{4}$/.test(y)) return "";
  const iso = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== iso) return "";
  return iso;
}

function OtpSignupForm({ emailOtpRequired, mobileOtpRequired, onDone }) {
  const [name, setName] = useState("");
  const [dob, setDob] = useState({ d: "", m: "", y: "" });
  const [mobileRaw, setMobileRaw] = useState("");
  const [mobileProof, setMobileProof] = useState(null);
  const [emailRaw, setEmailRaw] = useState("");
  const [emailProof, setEmailProof] = useState(null);
  const [terms, setTerms] = useState(false);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const mobileDigits = mobileRaw.replace(/\D/g, "");
  const emailOk = emailOtpRequired ? !!emailProof : EMAIL_RE.test(emailRaw.trim().toLowerCase());
  const mobileOk = mobileOtpRequired ? !!mobileProof : MOBILE_RE.test(mobileDigits);
  const dobIso = dobToIso(dob);
  const required = (emailOtpRequired ? 1 : 0) + (mobileOtpRequired ? 1 : 0);
  const done = (emailProof ? 1 : 0) + (mobileProof ? 1 : 0);
  const canSubmit = name.trim().length > 1 && emailOk && mobileOk && !!dobIso && terms && !submitting;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErr("");
    try {
      const { data } = await api.post("/api/auth/otp/register", {
        full_name: name.trim().replace(/\s+/g, " "),
        email: emailProof?.target || emailRaw.trim().toLowerCase(),
        mobile_number: mobileProof?.target || mobileDigits,
        dob: dobIso,
        accept_terms: terms,
        email_proof: emailProof?.proof || null,
        mobile_proof: mobileProof?.proof || null,
      });
      onDone(data);
    } catch (e2) {
      setErr(getErrorMessage(e2));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">Create your account</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {emailOtpRequired && mobileOtpRequired
            ? "Verify your email and mobile number to finish."
            : emailOtpRequired
              ? "Verify your email. You can verify your phone later from Profile."
              : mobileOtpRequired
                ? "Verify your mobile number. You can verify your email later from Profile."
                : "Enter your email and mobile number. You can verify them later from Profile."}
        </p>
      </div>

      {required > 0 && (
        <div className="flex items-center gap-2">
          {Array.from({ length: required }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 flex-1 rounded-full transition ${i < done ? "brand-gradient" : "bg-surface-2"}`}
            />
          ))}
          <span className="text-xs tabular-nums text-muted-foreground">
            {done}/{required} verified
          </span>
        </div>
      )}

      <div>
        <label className={labelClass} htmlFor="su-name">
          Full name
        </label>
        <input
          id="su-name"
          className={fieldClass}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="As on your government ID"
          autoComplete="name"
          maxLength={120}
          data-testid="su-name"
        />
      </div>

      {mobileOtpRequired ? (
        <VerifyField
          id="su-mobile"
          channel="mobile"
          labelText="Mobile number"
          placeholder="98765 43210"
          verified={!!mobileProof}
          onVerified={(target, proof) => setMobileProof({ target, proof })}
        />
      ) : (
        <div>
          <label className={labelClass} htmlFor="su-mobile">Mobile number</label>
          <input
            id="su-mobile"
            type="tel"
            className={fieldClass}
            value={mobileRaw}
            onChange={(e) => setMobileRaw(e.target.value)}
            placeholder="98765 43210"
            inputMode="numeric"
            autoComplete="tel-national"
            maxLength={14}
            data-testid="su-mobile-input"
          />
          <p className="mt-1 text-xs text-muted-foreground">Saved now. Verify it later from <strong>Profile</strong>.</p>
        </div>
      )}
      {emailOtpRequired ? (
        <VerifyField
          id="su-email"
          channel="email"
          labelText="Email ID"
          placeholder="you@company.com"
          verified={!!emailProof}
          onVerified={(target, proof) => setEmailProof({ target, proof })}
        />
      ) : (
        <div>
          <label className={labelClass} htmlFor="su-email">Email ID</label>
          <input
            id="su-email"
            type="email"
            className={fieldClass}
            value={emailRaw}
            onChange={(e) => setEmailRaw(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            maxLength={254}
            data-testid="su-email-input"
          />
          <p className="mt-1 text-xs text-muted-foreground">Saved now. Verify it later from <strong>Profile</strong>.</p>
        </div>
      )}

      <div>
        <label className={labelClass} htmlFor="su-dob-d">
          Date of birth
        </label>
        <div className="mt-1.5 grid grid-cols-3 gap-2">
          {[
            { k: "d", ph: "DD", max: 2 },
            { k: "m", ph: "MM", max: 2 },
            { k: "y", ph: "YYYY", max: 4 },
          ].map((p) => (
            <input
              key={p.k}
              id={`su-dob-${p.k}`}
              aria-label={p.ph}
              placeholder={p.ph}
              inputMode="numeric"
              maxLength={p.max}
              value={dob[p.k]}
              data-testid={`su-dob-${p.k}`}
              onChange={(e) => setDob({ ...dob, [p.k]: e.target.value.replace(/\D/g, "") })}
              className="rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-center text-sm tabular-nums text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/25"
            />
          ))}
        </div>
        {dob.y.length === 4 && !dobIso && <p className="mt-1 text-xs text-down">Enter a valid date.</p>}
      </div>

      <label className="flex items-start gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={terms}
          onChange={(e) => setTerms(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-primary"
          data-testid="su-terms"
        />
        <span>
          I agree to the{" "}
          <Link to="/terms" className="text-primary hover:underline">
            Terms of Service
          </Link>
          ,{" "}
          <Link to="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
          , AML/KYC Policy and the risk disclosure for crypto trading.
        </span>
      </label>

      <ErrorText msg={err} testId="register-error" />

      <button type="submit" className={ctaClass} disabled={!canSubmit} data-testid="signup-submit">
        {submitting && <Loader2 size={16} className="animate-spin" />}
        {submitting ? "Creating…" : "Create account"}
      </button>

      <p className="text-center text-sm text-muted-foreground">
        Already registered?{" "}
        <Link to="/login" className="text-primary hover:underline" data-testid="register-to-login">
          Log in
        </Link>
      </p>
    </form>
  );
}

function OtpDevHint({ label, code, note }) {
  if (!code) return null;
  return (
    <div
      className="text-[12px] bg-warning/10 text-warning border border-warning/30 rounded-lg px-4 py-3"
      data-testid="otp-dev-hint"
    >
      <strong>{label} verification code:</strong>{" "}
      <span className="font-mono text-[14px]">{code}</span>
      {note ? <span className="block mt-1 text-[11px] text-warning">{note}</span> : null}
    </div>
  );
}

/* =========================== Step 2: Email OTP =========================== */
function Step2Email({ onDone, onCancelSignup, email, initialDevOtp, onDevOtp }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [err, setErr] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [devOtp, setDevOtp] = useState(initialDevOtp || "");

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  async function verify(e) {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await api.post("/api/auth/email/verify-otp", { code }); onDone(); }
    catch (e2) { setErr(getErrorMessage(e2)); }
    finally { setLoading(false); }
  }

  async function resend() {
    setResending(true);
    try {
      const { data } = await api.post("/api/auth/email/resend-otp");
      setCooldown(60);
      if (data?.dev_email_otp) {
        setDevOtp(data.dev_email_otp);
        onDevOtp?.(data.dev_email_otp);
      }
    }
    catch (e2) { setErr(getErrorMessage(e2)); }
    finally { setResending(false); }
  }

  return (
    <form onSubmit={verify} className="space-y-5" data-testid="step2-form">
      <h2 className="text-[22px] font-semibold text-foreground tracking-tight">Verify your email</h2>
      <p className="text-muted-foreground text-[14px] -mt-2">
        We sent a 6-digit code to <span className="font-semibold text-foreground">{email}</span>.{" "}
        <button
          type="button"
          onClick={onCancelSignup}
          data-testid="step2-change-email"
          className="text-primary font-semibold hover:underline"
        >
          Change email
        </button>
      </p>

      <OtpDevHint
        label="Email"
        code={devOtp}
        note="This code is shown because Titan or Google mail is not configured, so it was not emailed."
      />
      {!devOtp ? (
        <p className="text-[12px] text-muted-foreground bg-surface-2 border border-border rounded-lg px-4 py-3">
          The code was emailed. If it is not in your inbox, check your <strong>spam folder</strong>.
        </p>
      ) : null}

      <Input label="Email verification code" inputMode="numeric" maxLength={6} required
             value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
             testid="step2-code" placeholder="______"
             className="text-center tracking-[0.4em] font-mono text-[18px]" />

      <ErrorBox msg={err} />

      <div className="flex items-center justify-between text-[13px]">
        <button type="button" onClick={resend} disabled={resending || cooldown > 0}
                data-testid="step2-resend"
                className="text-primary font-semibold hover:underline disabled:opacity-50 disabled:no-underline">
          {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? "Sending…" : "Resend code"}
        </button>
      </div>

      <PrimaryBtn loading={loading} testid="step2-submit">Verify email</PrimaryBtn>
    </form>
  );
}

/* =========================== Container =========================== */
export default function Register() {
  const nav = useNavigate();
  const { user, pendingUser, fetchMe, setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [otpRequired, setOtpRequired] = useState(true);
  const [emailOtpRequired, setEmailOtpRequired] = useState(true);
  const [devEmailOtp, setDevEmailOtp] = useState("");
  const [showVerifyLater, setShowVerifyLater] = useState(false);
  const completingRef = useRef(false);

  useEffect(() => {
    api.get("/api/auth/signup-config")
      .then(({ data }) => {
        setOtpRequired(data.otp_verification_required !== false);
        setEmailOtpRequired(data.signup_email_otp_required !== false);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (completingRef.current) return;
    if (user) {
      nav("/markets", { replace: true });
      return;
    }
    if (pendingUser === null) {
      setStep(1);
      return;
    }
    if (!pendingUser.email_verified) setStep(2);
    else if (otpRequired ? !pendingUser.mobile_verified : true) setStep(3);
  }, [user, pendingUser, nav, otpRequired]);

  async function refreshThen(next) {
    await fetchMe();
    setStep(next);
  }

  function enterAppAfterOtpSignup(data) {
    completingRef.current = true;
    if (data.access_token) setUserTokens(data.access_token, data.refresh_token);
    const otpFlag = data.otp_verification_required !== false;
    setUser(data.user, otpFlag);
    if (!data.user?.mobile_verified) {
      setShowVerifyLater(true);
      return;
    }
    nav("/markets", { replace: true, state: { justRegistered: true } });
  }

  async function enterAppAfterSignup({ skippedOtp } = {}) {
    completingRef.current = true;
    const me = await fetchMe();
    if (skippedOtp || !otpRequired) {
      setUser(me, false);
    }
    nav("/markets", { replace: true, state: { justRegistered: true } });
  }

  async function cancelSignup() {
    if (!window.confirm("This will delete your in-progress signup so you can start over with a different email. Continue?")) return;
    try { await api.post("/api/auth/cancel-signup"); } catch { /* force clean state */ }
    await fetchMe();
    setStep(1);
  }

  const steps = buildSteps(otpRequired);
  const mobileStep = 3;

  return (
    <AuthPageLayout layoutTestId="register-layout">
      {showVerifyLater && (
        <VerifyLaterPopup
          onContinue={async () => {
            setShowVerifyLater(false);
            await enterAppAfterSignup({ skippedOtp: true });
          }}
        />
      )}

      <div
        className="glass-panel page-fade relative w-full overflow-hidden rounded-2xl"
        data-testid="register-card"
      >
        <div className="p-6">
          {step === 1 ? (
            <OtpSignupForm
              emailOtpRequired={emailOtpRequired}
              mobileOtpRequired={otpRequired}
              onDone={enterAppAfterOtpSignup}
            />
          ) : (
            <>
              <h1 className="text-2xl font-semibold text-foreground">
                Finish signing up
              </h1>
              <p className="mt-2 text-[14px] text-muted-foreground">
                Verify your email{otpRequired ? " and mobile" : ""} to start exploring — KYC
                unlocks trading.
              </p>
              <div className="mt-6">
                <Stepper current={step} steps={steps} />
              </div>
            </>
          )}
          {step === 2 && (
            <Step2Email
              email={pendingUser?.email}
              initialDevOtp={devEmailOtp}
              onDevOtp={setDevEmailOtp}
              onDone={() => refreshThen(3)}
              onCancelSignup={cancelSignup}
            />
          )}
          {step === mobileStep && (
            <Step3Mobile
              otpRequired={otpRequired}
              onDone={async ({ skippedOtp }) => {
                if (skippedOtp) {
                  completingRef.current = true;
                  const me = await fetchMe();
                  setUser(me, false);
                  setShowVerifyLater(true);
                } else {
                  await enterAppAfterSignup({ skippedOtp: false });
                }
              }}
            />
          )}
        </div>
      </div>
    </AuthPageLayout>
  );
}
